package songwritersnotebook.app.viewmodels

import androidx.lifecycle.ViewModel
import songwritersnotebook.app.data.ChordDefinition
import songwritersnotebook.app.data.GuitarTab
import songwritersnotebook.app.data.TabColumn
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import com.benasher44.uuid.uuid4

import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.crdt.UndoScope

import songwritersnotebook.app.data.UserPresence

enum class TabInputMode { KEYBOARD, FRETBOARD }

data class TabEditorUiState(
    val tab: GuitarTab = GuitarTab(),
    val cursorColumn: Int = 0,
    val cursorString: Int = 0, // 0 = High E (Top), 5 = Low E (Bottom)
    val availableChords: List<ChordDefinition> = emptyList(),
    val isInstructionRowFocused: Boolean = false,
    val inputMode: TabInputMode = TabInputMode.KEYBOARD,
    val fretWindowStart: Int = 0,
    val fretboardSelection: Map<Int, Int> = emptyMap(), // StringIndex -> FretNumber
    val remotePresences: List<UserPresence> = emptyList()
)

class TabEditorViewModel(
    initialTab: GuitarTab,
    availableChords: List<ChordDefinition>,
    private val songId: String = "",
    private val targetId: String = "",
    private val tabId: String = "",
    private val onTabChange: (GuitarTab) -> Unit = {}
) : ViewModel() {
    private val dependencyProvider = songwritersnotebook.app.di.PlatformDependencyProvider()
    private val songRepository = dependencyProvider.getSongRepository()
    private val sessionDeviceId = songRepository?.sessionDeviceId ?: com.benasher44.uuid.uuid4().toString()
    private val preferencesManager = songwritersnotebook.app.utils.PreferencesManager()
    private val currentUserId = songRepository?.getCurrentUserId()

    private val _uiState = MutableStateFlow(
        TabEditorUiState(
            // Initialize with correct number of columns if empty
            tab = initialTab.takeIf { it.columns.isNotEmpty() }
                ?: initialTab.copy(columns = List(initialTab.beats * initialTab.subdivisions) { TabColumn() }),
            availableChords = availableChords
        )
    )
    val uiState = _uiState.asStateFlow()

    private val _playTrigger = kotlinx.coroutines.channels.Channel<Unit>(kotlinx.coroutines.channels.Channel.BUFFERED)
    val playTrigger = _playTrigger.receiveAsFlow()

    private var songObservationJob: Job? = null
    private var presenceHeartbeatJob: Job? = null
    private var isApplyingRemoteChange = false
    private var isCollaborationEnabled = false

    init {
        // Sync initial selection
        syncFretboardSelection(force = true)
        
        if (songId.isNotEmpty()) {
            startPresenceHeartbeat()
            observeSong()
        }
    }

    private fun startPresenceHeartbeat() {
        if (songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return
        presenceHeartbeatJob?.cancel()
        presenceHeartbeatJob = viewModelScope.launch {
            while (isActive) {
                val state = _uiState.value
                val currentUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
                val generatedColor = songwritersnotebook.app.data.UserPresence.generateUserColor(currentUserId ?: "anonymous")
                
                val presence = songwritersnotebook.app.data.UserPresence(
                    userId = currentUserId ?: "anonymous",
                    email = currentUser?.email ?: "",
                    deviceId = sessionDeviceId,
                    username = currentUser?.displayName ?: "",
                    color = generatedColor,
                    activeTabId = tabId,
                    tabCursorColumn = state.cursorColumn,
                    tabCursorString = state.cursorString,
                    activeSectionId = if (state.isInstructionRowFocused) "instruction" else null,
                    lastActive = kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toDouble()
                )
                songRepository?.updatePresence(songId, presence)
                delay(30000) // 30s heartbeat instead of 10s
            }
        }
    }

    private var presenceUpdateJob: Job? = null

    private fun updatePresence() {
        if (!isCollaborationEnabled && _uiState.value.remotePresences.isEmpty()) return
        if (songId.isEmpty() || songId == songwritersnotebook.app.tutorial.TutorialManager.TUTORIAL_SONG_ID) return
        val state = _uiState.value
        val currentUser = songwritersnotebook.app.utils.AuthHandler().getCurrentUser()
        val generatedColor = songwritersnotebook.app.data.UserPresence.generateUserColor(currentUserId ?: "anonymous")
        
        val presence = songwritersnotebook.app.data.UserPresence(
            userId = currentUserId ?: "anonymous",
            email = currentUser?.email ?: "",
            deviceId = sessionDeviceId,
            username = currentUser?.displayName ?: "",
            color = generatedColor,
            activeTabId = tabId,
            tabCursorColumn = state.cursorColumn,
            tabCursorString = state.cursorString,
            activeSectionId = if (state.isInstructionRowFocused) "instruction" else null,
            lastActive = kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toDouble()
        )
        
        presenceUpdateJob?.cancel()
        presenceUpdateJob = viewModelScope.launch {
            delay(1000) // 1000ms debounce
            songRepository?.updatePresence(songId, presence)
        }
    }

    private fun observeSong() {
        songObservationJob?.cancel()
        songObservationJob = viewModelScope.launch {
            val initialSong = songRepository?.getSongById(songId)
            if (initialSong != null) {
                isCollaborationEnabled = initialSong.ownerId.isNotEmpty() && (initialSong.collaborators.isNotEmpty() || initialSong.viewers.isNotEmpty())
            }
            songRepository?.observeSong(songId)?.collect { song ->
                if (song != null && !isApplyingRemoteChange) {
                    processSongUpdate(song)
                }
            }
        }
    }

    private fun processSongUpdate(song: Song) {
        isCollaborationEnabled = song.ownerId.isNotEmpty() && (song.collaborators.isNotEmpty() || song.viewers.isNotEmpty())
        // Find our tab in the song
        var foundTab: GuitarTab? = null
        for (section in song.sections) {
            if (section.id == targetId) {
                foundTab = section.tabs.find { it.id == tabId }
            } else {
                for (line in section.lines) {
                    if (line.id == targetId) {
                        foundTab = line.tabs.find { it.id == tabId }
                    }
                }
            }
            if (foundTab != null) break
        }

        if (foundTab != null && foundTab != _uiState.value.tab) {
            isApplyingRemoteChange = true
            _uiState.update { it.copy(tab = foundTab!!) }
            syncFretboardSelection()
            isApplyingRemoteChange = false
        }
        
        // Update remote presence in UI (filter out self and STALE presences > 60s)
        val now = kotlinx.datetime.Clock.System.now().toEpochMilliseconds().toDouble()
        val remotePresence = song.presence.values.filter { 
            it.deviceId != sessionDeviceId && it.activeTabId == tabId && (now - it.lastActive) < 60000 
        }
        _uiState.update { it.copy(remotePresences = remotePresence) }
    }

    private fun pushCrdtUpdate() {
        // Now delegating to EditorViewModel via callback to prevent split-brain fetches
        onTabChange(_uiState.value.tab)
        updatePresence()
    }

    private fun syncFretboardSelection(force: Boolean = false) {
        _uiState.update { state ->
            val col = state.tab.columns.getOrNull(state.cursorColumn)
            // If column is empty/blank, and we are not forcing, keep existing selection
            // We define "empty" as all notes being "-" or "x" (actually x is a valid selection, but - is not)
            // Let's say if ALL are "-", we assume it's blank.
            val hasNotes = col?.notes?.any { it != "-" } == true
            
            if (!force && !hasNotes) {
                state
            } else {
                val newSelection = mutableMapOf<Int, Int>()
                col?.notes?.forEachIndexed { index, note ->
                    if (note.equals("x", ignoreCase = true)) {
                        newSelection[index] = -1 // Muted
                    } else {
                        val fret = note.toIntOrNull()
                        if (fret != null) {
                            newSelection[index] = fret
                        }
                    }
                }
                
                // Auto-scroll fret window to lowest non-zero fret
                val minFret = newSelection.values.filter { it > 0 }.minOrNull()
                val newStart = if (minFret != null) {
                    (minFret - 1).coerceIn(0, 18)
                } else {
                    state.fretWindowStart // Keep current if no notes or only open/muted
                }

                state.copy(
                    fretboardSelection = newSelection,
                    fretWindowStart = newStart
                )
            }
        }
    }

    // --- Navigation ---

    fun moveCursor(deltaRow: Int, deltaCol: Int): Boolean {
        var changed = false
        _uiState.update { state ->
            // Horizontal
            val requestedCol = state.cursorColumn + deltaCol
            var currentTab = state.tab
            var newCol = requestedCol

            if (requestedCol > currentTab.columns.lastIndex) {
                // Auto-add columns if moving right past the end
                val needed = requestedCol - currentTab.columns.lastIndex
                val newCols = List(needed) { TabColumn(id = uuid4().toString()) }
                currentTab = currentTab.copy(columns = currentTab.columns + newCols)
            } else {
                newCol = requestedCol.coerceAtLeast(0)
            }

            // Vertical Logic
            var newString = state.cursorString
            var newInstructionFocus = state.isInstructionRowFocused

            if (deltaRow != 0) {
                if (state.isInstructionRowFocused) {
                    if (deltaRow > 0) {
                        newInstructionFocus = false
                        newString = 0 // Down from instructions -> String 0
                    }
                } else {
                    val requestedString = state.cursorString + deltaRow
                    if (requestedString < 0) {
                        newInstructionFocus = true // Up from String 0 -> Instructions
                    } else {
                        newString = requestedString.coerceIn(0, 5)
                    }
                }
            }

            if (currentTab != state.tab || newCol != state.cursorColumn || newString != state.cursorString || newInstructionFocus != state.isInstructionRowFocused) {
                changed = true
                state.copy(
                    tab = currentTab,
                    cursorString = newString,
                    cursorColumn = newCol,
                    isInstructionRowFocused = newInstructionFocus
                )
            } else {
                state
            }
        }
        if (changed) {
            syncFretboardSelection() // Sync after move
            updatePresence()
        }
        return changed
    }

    // Used for Tapping (Absolute movement)
    fun jumpTo(col: Int, string: Int, isInstruction: Boolean) {
        _uiState.update { state ->
            state.copy(
                cursorColumn = col.coerceIn(0, state.tab.columns.lastIndex),
                cursorString = string.coerceIn(0, 5),
                isInstructionRowFocused = isInstruction
            )
        }
        syncFretboardSelection() // Sync after jump
        updatePresence()
    }

    // --- Fretboard & Input Mode ---

    fun toggleInputMode() {
        _uiState.update { it.copy(inputMode = if (it.inputMode == TabInputMode.KEYBOARD) TabInputMode.FRETBOARD else TabInputMode.KEYBOARD) }
    }

    fun updateFretWindow(newStart: Int) {
        _uiState.update { it.copy(fretWindowStart = newStart.coerceIn(0, 18)) }
    }

    fun toggleFretboardNote(stringIndex: Int, fret: Int) {
        _uiState.update { state ->
            val currentSelection = state.fretboardSelection.toMutableMap()
            if (fret == 0) {
                // Nut Logic: Unselected -> 0 -> -1 (Muted) -> Unselected
                val currentValue = currentSelection[stringIndex]
                when (currentValue) {
                    null -> currentSelection[stringIndex] = 0
                    0 -> currentSelection[stringIndex] = -1
                    -1 -> currentSelection.remove(stringIndex)
                    else -> currentSelection[stringIndex] = 0 // Should not happen if other frets are cleared, but safer to set to 0
                }
            } else {
                // Regular Fret Logic: Toggle On/Off
                // Also clear any Nut/Mute selection on this string if selecting a fret
                if (currentSelection[stringIndex] == fret) {
                    currentSelection.remove(stringIndex)
                } else {
                    currentSelection[stringIndex] = fret
                }
            }
            state.copy(fretboardSelection = currentSelection)
        }
        _playTrigger.trySend(Unit)
    }

    fun commitFretboardSelection() {
        _uiState.update { state ->
            val colIndex = state.cursorColumn
            val newColumns = state.tab.columns.toMutableList()

            if (colIndex in newColumns.indices) {
                val oldColumn = newColumns[colIndex]
                val newNotes = MutableList(6) { index ->
                    val sel = state.fretboardSelection[index]
                    when (sel) {
                        null -> "-"
                        -1 -> "x"
                        else -> sel.toString()
                    }
                }
                newColumns[colIndex] = oldColumn.copy(notes = newNotes)
                
                // Auto-advance cursor
                val nextCol = colIndex + 1
                if (nextCol > newColumns.lastIndex) {
                    newColumns.add(TabColumn(id = uuid4().toString()))
                }
                
                state.copy(
                    tab = state.tab.copy(columns = newColumns),
                    cursorColumn = nextCol
                )
            } else state
        }
        syncFretboardSelection() // Re-sync from new column (likely empty/partial)
        pushCrdtUpdate()
    }

    fun clearFretboardSelection() {
        _uiState.update { it.copy(fretboardSelection = emptyMap()) }
    }

    // --- Editing ---

    fun updateTabName(newName: String) {
        val updatedTab = _uiState.value.tab.copy(name = newName)
        _uiState.update { it.copy(tab = updatedTab) }
        onTabChange(updatedTab)
        updatePresence()
    }

    fun updateTabSettings(subdivisions: Int, beats: Int) {
        _uiState.update { state ->
            val targetCount = beats * subdivisions
            val currentCount = state.tab.columns.size

            // Resize columns list
            val newColumns = if (targetCount > currentCount) {
                // Grow: Add empty columns
                state.tab.columns + List(targetCount - currentCount) { TabColumn(id = uuid4().toString()) }
            } else {
                // Shrink: Trim the list (warning: deletes data at end)
                state.tab.columns.take(targetCount)
            }

            state.copy(
                tab = state.tab.copy(
                    subdivisions = subdivisions,
                    beats = beats,
                    columns = newColumns
                )
            )
        }
        pushCrdtUpdate()
    }

    fun updateInstruction(text: String) {
        _uiState.update { state ->
            val colIndex = state.cursorColumn
            val newColumns = state.tab.columns.toMutableList()

            if (colIndex in newColumns.indices) {
                newColumns[colIndex] = newColumns[colIndex].copy(instruction = text)
                state.copy(tab = state.tab.copy(columns = newColumns))
            } else state
        }
        pushCrdtUpdate()
    }

    fun updateNote(newText: String) {
        _uiState.update { state ->
            val colIndex = state.cursorColumn
            val strIndex = state.cursorString
            val newColumns = state.tab.columns.toMutableList()

            if (colIndex in newColumns.indices) {
                val oldColumn = newColumns[colIndex]
                val newNotes = oldColumn.notes.toMutableList()

                newNotes[strIndex] = newText
                newColumns[colIndex] = oldColumn.copy(notes = newNotes)

                state.copy(tab = state.tab.copy(columns = newColumns))
            } else state
        }
        pushCrdtUpdate()
    }

    fun addColumn() {
        _uiState.update { state ->
            val tab = state.tab
            
            // Calculate which beat the cursor is in
            var currentStart = 0
            var beatIndex = 0
            // Loop to find beat
            // Safety limit
            val maxBeats = (tab.columns.size / 1.coerceAtLeast(tab.subdivisions)) + 50
            for (i in 0 until maxBeats) {
                val sub = tab.beatSubdivisions[i.toString()] ?: tab.subdivisions
                val beatEnd = currentStart + sub
                if (state.cursorColumn < beatEnd) {
                    beatIndex = i
                    break
                }
                currentStart += sub
                beatIndex = i + 1
            }
            
            val currentSub = tab.beatSubdivisions[beatIndex.toString()] ?: tab.subdivisions
            val newBeatSubs = tab.beatSubdivisions.toMutableMap()
            newBeatSubs[beatIndex.toString()] = currentSub + 1
            
            val newColumns = state.tab.columns.toMutableList()
            newColumns.add(state.cursorColumn + 1, TabColumn(id = uuid4().toString()))
            
            state.copy(
                tab = state.tab.copy(
                    columns = newColumns,
                    beatSubdivisions = newBeatSubs
                ),
                cursorColumn = state.cursorColumn + 1
            )
        }
        pushCrdtUpdate()
    }

    fun deleteColumn() {
        _uiState.update { state ->
            if (state.tab.columns.size <= 1) return@update state

            // Calculate which beat the cursor is in
            val tab = state.tab
            var currentStart = 0
            var beatIndex = 0
            val maxBeats = (tab.columns.size / 1.coerceAtLeast(tab.subdivisions)) + 50
            for (i in 0 until maxBeats) {
                val sub = tab.beatSubdivisions[i.toString()] ?: tab.subdivisions
                val beatEnd = currentStart + sub
                if (state.cursorColumn < beatEnd) {
                    beatIndex = i
                    break
                }
                currentStart += sub
                beatIndex = i + 1
            }

            val currentSub = tab.beatSubdivisions[beatIndex.toString()] ?: tab.subdivisions
            val newBeatSubs = tab.beatSubdivisions.toMutableMap()
            
            if (currentSub > 1) {
                newBeatSubs[beatIndex.toString()] = currentSub - 1
            } else {
                 newBeatSubs[beatIndex.toString()] = currentSub - 1
            }

            val newColumns = state.tab.columns.toMutableList()
            newColumns.removeAt(state.cursorColumn)

            val newCursor = state.cursorColumn.coerceAtMost(newColumns.lastIndex)

            state.copy(
                tab = state.tab.copy(
                    columns = newColumns,
                    beatSubdivisions = newBeatSubs
                ),
                cursorColumn = newCursor
            )
        }
        pushCrdtUpdate()
    }

    fun injectChord(chordName: String) {
        val chord = _uiState.value.availableChords.find { it.name == chordName } ?: return
        val tabData = chord.guitarTab ?: songwritersnotebook.app.utils.ChordUtils.GUITAR_CHORD_TABS[chord.name]?.map { it.toString() } ?: return
        if (tabData.size != 6) return

        if (_uiState.value.inputMode == TabInputMode.FRETBOARD) {
            // FRETBOARD MODE: Only update selection
            _uiState.update { state ->
                val visualOrderNotes = tabData.reversed()
                val newSelection = mutableMapOf<Int, Int>()
                
                visualOrderNotes.forEachIndexed { index, note ->
                     if (note.equals("x", ignoreCase = true)) {
                         newSelection[index] = -1
                     } else {
                         val fret = note.toIntOrNull()
                         if (fret != null) {
                             newSelection[index] = fret
                         }
                     }
                }
                
                state.copy(fretboardSelection = newSelection)
            }
            _playTrigger.trySend(Unit)
        } else {
            // KEYBOARD MODE: Update tab directly
            _uiState.update { state ->
                val visualOrderNotes = tabData.reversed()
                val oldColumn = state.tab.columns[state.cursorColumn]
                val newNotes = visualOrderNotes.map { if (it.lowercase() == "x") "x" else it }
    
                val newColumns = state.tab.columns.toMutableList()
                newColumns[state.cursorColumn] = oldColumn.copy(notes = newNotes)
    
                state.copy(tab = state.tab.copy(columns = newColumns))
            }
            syncFretboardSelection()
            pushCrdtUpdate()
        }
    }

    override fun onCleared() {
        super.onCleared()
        // Removed stopSongObservation as it kills the primary sync session for the parent EditorViewModel.
    }
}
